import React, { useState, useEffect } from "react";
import { BiGift, BiX, BiInfoCircle } from "react-icons/bi";
import { useServiceStore } from "./../../store/serviceStore";
import { toast } from "react-toastify";
import useGiftCardStore from "./../../store/useGiftCardStore";

const GiftCardCreateForm = ({ onSuccess, onCancel }) => {
  const { createGiftCard, isCreating } = useGiftCardStore();
  const { services, loadServices } = useServiceStore();

  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // Mettre à jour les détails du service sélectionné
  useEffect(() => {
    if (selectedServiceId) {
      const service = services.find(
        (s) => s.id === parseInt(selectedServiceId),
      );
      setSelectedServiceDetails(service || null);
    } else {
      setSelectedServiceDetails(null);
    }
  }, [selectedServiceId, services]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validation
      if (!selectedServiceId) {
        toast.error("Veuillez sélectionner un service");
        setIsSubmitting(false);
        return;
      }

      if (quantity < 1) {
        toast.error("La quantité doit être au moins de 1");
        setIsSubmitting(false);
        return;
      }

      if (quantity > 100) {
        toast.warning("La quantité maximale est de 100 sous-cartes");
        setIsSubmitting(false);
        return;
      }

      // Formater les données pour l'API
      const cardData = {
        serviceId: parseInt(selectedServiceId),
        quantity: quantity,
      };

      await createGiftCard(cardData);

      // Réinitialiser le formulaire
      setSelectedServiceId("");
      setQuantity(1);
      setSelectedServiceDetails(null);

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      toast.error("Erreur lors de la création de la carte cadeau");
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateTotal = () => {
    if (!selectedServiceDetails) return 0;
    return selectedServiceDetails.price * quantity;
  };

  // Filtrer les services actifs
  const activeServices = services.filter((s) => s.is_active);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="p-2 md:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Nouvelle Carte Cadeau
            </h2>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100 transition-colors"
            >
              <BiX size={24} />
            </button>
          </div>
        </div>

        <div className="p-2 md:p-6">
          {/* Message d'information */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200">
            <div className="flex items-start gap-3">
              <BiInfoCircle className="text-blue-600 mt-0.5" size={20} />
              <div>
                <p className="font-nunito font-medium text-[14px] text-text">
                  Créez une carte cadeau contenant un service
                </p>
                <p className="font-nunito text-[13px] text-gray-600 mt-1">
                  La quantité représente le nombre de sous-cartes cadeau à
                  générer pour ce service. Chaque sous-carte aura un code unique
                  et pourra être utilisée individuellement.
                </p>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Sélection du service */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                  Service *
                </label>
                <select
                  value={selectedServiceId}
                  onChange={(e) => setSelectedServiceId(e.target.value)}
                  required
                  className="w-full border border-gray-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px] text-text"
                >
                  <option value="">Sélectionner un service</option>
                  {activeServices.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.nom} -{" "}
                      {parseFloat(service.price).toLocaleString()} FCFA
                    </option>
                  ))}
                </select>
                <p className="font-nunito text-xs text-gray-500 mt-1">
                  Sélectionnez le service à inclure dans la carte cadeau
                </p>
              </div>

              {/* Détails du service sélectionné */}
              {selectedServiceDetails && (
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <h3 className="font-poppins font-medium text-[15px] text-subtitle mb-3">
                    Détails du service sélectionné
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-nunito text-xs text-text mb-1">Nom</p>
                      <p className="font-poppins font-medium">
                        {selectedServiceDetails.nom}
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-xs text-text mb-1">Code</p>
                      <p className="font-mono text-sm">
                        {selectedServiceDetails.code}
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-xs text-text mb-1">
                        Prix unitaire
                      </p>
                      <p className="font-poppins font-medium text-secondary-600">
                        {parseFloat(
                          selectedServiceDetails.price,
                        ).toLocaleString()}{" "}
                        FCFA
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-xs text-text mb-1">
                        Durée
                      </p>
                      <p className="font-nunito text-sm">
                        {selectedServiceDetails.durationMinutes} minutes
                      </p>
                    </div>
                    {selectedServiceDetails.description && (
                      <div className="md:col-span-2">
                        <p className="font-nunito text-xs text-text mb-1">
                          Description
                        </p>
                        <p className="font-nunito text-sm">
                          {selectedServiceDetails.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quantité de sous-cartes */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                  Nombre de sous-cartes *
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      max="100"
                      value={quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value) {
                          setQuantity(Math.min(Math.max(value, 1), 100));
                        } else {
                          setQuantity("");
                        }
                      }}
                      required
                      className="w-full border border-gray-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px] text-text"
                    />
                  </div>
                  <div className="w-32 text-right">
                    <p className="font-nunito text-xs text-text mb-1">Total</p>
                    <p className="font-poppins font-bold text-lg text-secondary-600">
                      {quantity}
                    </p>
                  </div>
                </div>
                <p className="font-nunito text-xs text-gray-500 mt-1">
                  Nombre de codes uniques à générer pour ce service (1-100)
                </p>
              </div>

              {/* Résumé et calculs */}
              <div className="bg-gray-50 p-6 border border-gray-200">
                <h3 className="font-poppins font-semibold text-[16px] text-title mb-4">
                  Résumé de la commande
                </h3>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-nunito text-sm text-text">
                      Service
                    </span>
                    <span className="font-poppins font-medium">
                      {selectedServiceDetails?.nom ||
                        "Aucun service sélectionné"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="font-nunito text-sm text-text">
                      Prix unitaire
                    </span>
                    <span className="font-poppins font-medium">
                      {selectedServiceDetails
                        ? `${parseFloat(selectedServiceDetails.price).toLocaleString()} FCFA`
                        : "0 FCFA"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="font-nunito text-sm text-text">
                      Nombre de sous-cartes
                    </span>
                    <span className="font-poppins font-medium">{quantity}</span>
                  </div>

                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-poppins font-semibold text-lg text-title">
                        Valeur totale
                      </span>
                      <span className="font-poppins font-bold text-xl text-secondary-600">
                        {calculateTotal().toLocaleString()} FCFA
                      </span>
                    </div>
                    <p className="font-nunito text-xs text-gray-500 mt-1">
                      {quantity} sous-carte{quantity !== 1 ? "s" : ""} cadeau à
                      générer
                    </p>
                  </div>
                </div>
              </div>

              {/* Informations supplémentaires */}
              <div className="bg-secondary-50 p-4 border border-secondary-200">
                <div className="flex items-center gap-2 mb-2">
                  <BiGift className="text-secondary-600" size={20} />
                  <span className="font-poppins font-medium text-[15px] text-subtitle">
                    Comment fonctionnent les cartes cadeau ?
                  </span>
                </div>
                <ul className="font-nunito text-[13px] text-gray-600 space-y-1">
                  <li>
                    • Un code principal sera généré pour regrouper toutes les
                    sous-cartes
                  </li>
                  <li>
                    • Chaque sous-carte aura son propre code unique (ex:
                    CODE_PRINCIPAL_C001)
                  </li>
                  <li>
                    • Les sous-cartes peuvent être utilisées individuellement
                  </li>
                  <li>
                    • Vous pourrez vérifier et gérer chaque sous-carte depuis la
                    liste
                  </li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-3 text-gray-600 border border-gray-300 hover:bg-gray-50 font-nunito font-semibold text-[14px] transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!selectedServiceId || isSubmitting || isCreating}
                className="px-4 py-3 bg-secondary-600 text-white hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-nunito font-semibold text-[14px] transition-colors"
              >
                {isSubmitting || isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Création en cours...
                  </>
                ) : (
                  <>
                    <BiGift size={18} />
                    Créer la carte cadeau
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GiftCardCreateForm;

// import React, { useState, useEffect } from "react";
// import { BiPlus, BiTrash, BiGift, BiX } from "react-icons/bi";
// import { useServiceStore } from "./../../store/serviceStore";
// import { toast } from "react-toastify";
// import useGiftCardStore from "./../../store/useGiftCardStore";

// const GiftCardCreateForm = ({ onSuccess, onCancel }) => {
//   const { createGiftCard, isCreating } = useGiftCardStore();
//   const { services, loadServices } = useServiceStore();

//   const [selectedServices, setSelectedServices] = useState();
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   useEffect(() => {
//     loadServices();
//   }, [loadServices]);

//   const handleAddService = (serviceId) => {
//     if (!serviceId) return;

//     const service = services.find((s) => s.id === parseInt(serviceId));
//     if (service) {
//       // Vérifier si le service est déjà sélectionné
//       // const existingService = selectedServices.find(
//       //   (s) => s.serviceId === serviceId
//       // );
//       // if (existingService) {
//       //   // Augmenter la quantité si déjà présent
//       //   const updatedServices = selectedServices.map((s) =>
//       //     s.serviceId === serviceId ? { ...s, quantity: s.quantity + 1 } : s
//       //   );
//       //   setSelectedServices(updatedServices);
//       // } else {
//       //   // Ajouter nouveau service
//       // }
//       setSelectedServices(
//         {
//           serviceId: serviceId,
//           quantity: 1,
//           nom: service.nom,
//           price: service.price,
//         },
//       );
//     }
//   };

//   const handleRemoveService = (index) => {
//     // const newServices = selectedServices.filter((_, i) => i !== index);
//     setSelectedServices({});
//   };

//   // const updateServiceQuantity = (index, quantity) => {
//   //   const newServices = [...selectedServices];
//   //   newServices[index].quantity = quantity > 0 ? quantity : 1;
//   //   setSelectedServices(newServices);
//   // };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);

//     try {
//       // Valider qu'au moins un service est sélectionné
//       // const validServices = selectedServices.filter(
//       //   (service) => service.serviceId && service.quantity > 0
//       // );

//       // if (validServices.length === 0) {
//       //   toast.error("Veuillez ajouter au moins un service valide");
//       //   return;
//       // }

//       // Formater les données pour l'API
//       const servicesData = validServices.map((service) => ({
//         serviceId: parseInt(service.serviceId),
//         quantity: service.quantity,
//       }));

//       await createGiftCard(servicesData);
//       setSelectedServices([]);
//       if (onSuccess) onSuccess();
//     } catch (error) {
//       console.error("Erreur lors de la création:", error);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const calculateTotal = () => {
//     return selectedServices.reduce((total, service) => {
//       return total + service.price * service.quantity;
//     }, 0);
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//       <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto">
//         {/* En-tête */}
//         <div className="p-2 md:p-6 border-b border-gray-200">
//           <div className="flex items-center justify-between">
//             <h2 className="font-poppins font-semibold text-[18px] text-title">Nouvelle Carte Cadeau</h2>
//             <button
//               onClick={onCancel}
//               className="p-2 hover:bg-gray-100 transition-colors"
//             >
//               <BiX size={24} />
//             </button>
//           </div>
//         </div>

//         <div className="p-2 md:p-6">
//           {/* Message d'information */}
//           <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
//             <div className="flex items-center gap-2">
//               <BiGift className="text-blue-600" size={20} />
//               <span className="font-nunito text-[14px] text-text">
//                 Créez une carte cadeau contenant un ou plusieurs services
//               </span>
//             </div>
//           </div>

//           {/* Ajout de services */}
//           <div className="mb-6">
//             <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
//               Ajouter un service
//             </label>
//             <div className="flex gap-2">
//               <select
//                 onChange={(e) => {
//                   handleAddService(e.target.value);
//                   e.target.value = "";
//                 }}
//                 className="flex-1 border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px] text-text"
//               >
//                 <option value="">Sélectionner un service</option>
//                 {services
//                   .filter((s) => s.is_active)
//                   .map((service) => (
//                     <option key={service.id} value={service.id}>
//                       {service.nom} - {service.price.toLocaleString()} FCFA
//                     </option>
//                   ))}
//               </select>
//             </div>
//           </div>

//           {/* Liste des services sélectionnés */}
//           {selectedServices.length > 0 && (
//             <div className="mb-6">
//               <h3 className="font-poppins font-semibold text-[18px] text-title mb-3">
//                 Services inclus dans la carte
//               </h3>
//               <div className="space-y-3">
//                 {selectedServices.map((service, index) => (
//                   <div
//                     key={index}
//                     className="flex items-center justify-between p-3 border border-gray-200"
//                   >
//                     <div className="flex-1">
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">{service.nom}</p>
//                       <p className="font-nunito text-[14px] text-text text-sm">
//                         {service.price.toLocaleString()}
//                         FCFA × {service.quantity} =
//                         {(service.price * service.quantity).toLocaleString()}{" "}
//                         FCFA
//                       </p>
//                     </div>
//                     <div className="flex items-center gap-3">
//                       <div className="flex items-center gap-2">
//                         <label className="font-nunito text-[14px] text-text text-sm">
//                           Quantité:
//                         </label>
//                         <input
//                           type="number"
//                           min="1"
//                           value={service.quantity}
//                           onChange={(e) =>
//                             updateServiceQuantity(
//                               index,
//                               parseInt(e.target.value) || 1
//                             )
//                           }
//                           className="w-20 border border-gray-300 px-2 py-1 text-center font-nunito text-[14px] text-text"
//                         />
//                       </div>
//                       <button
//                         onClick={() => handleRemoveService(index)}
//                         className="text-red-600 hover:text-red-800 p-1"
//                       >
//                         <BiTrash size={18} />
//                       </button>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}

//           {/* Total */}
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
//             <div className="bg-gray-50 p-4">
//               <div className="flex justify-between items-center mb-2">
//                 <span className="font-poppins font-semibold text-[18px] text-title">
//                   Valeur totale de la carte:
//                 </span>
//                 <span className="font-poppins font-bold text-[16px] text-montant text-2xl">
//                   {calculateTotal().toLocaleString()} FCFA
//                 </span>
//               </div>
//               <p className="font-nunito text-[14px] text-text text-sm">
//                 {selectedServices.length} service
//                 {selectedServices.length !== 1 ? "s" : ""} inclus
//                 {selectedServices.length !== 1 ? "s" : ""}
//               </p>
//             </div>

//             <div className="bg-secondary-50 p-4 border border-secondary-200">
//               <div className="flex items-center gap-2 mb-2">
//                 <BiGift className="text-secondary-600" size={20} />
//                 <span className="font-poppins font-semibold text-[18px] text-title">
//                   À propos des cartes cadeau
//                 </span>
//               </div>
//               <p className="font-nunito text-[14px] text-text text-sm">
//                 La carte générera un code unique qui pourra être utilisé pour
//                 bénéficier des services inclus.
//               </p>
//             </div>
//           </div>
//         </div>

//         <div className="p-2 md:p-6 border-t border-gray-200 flex justify-end gap-3">
//           <button
//             onClick={onCancel}
//             className="px-4 py-2 text-gray-600 border border-gray-300 hover:bg-gray-50 font-nunito font-semibold text-[14px]"
//           >
//             Annuler
//           </button>
//           <button
//             onClick={handleSubmit}
//             disabled={
//               selectedServices.length === 0 || isSubmitting || isCreating
//             }
//             className="px-4 py-2 bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-nunito font-semibold text-[14px]"
//           >
//             {isSubmitting || isCreating ? (
//               <>
//                 <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
//                 Création en cours...
//               </>
//             ) : (
//               <>
//                 <BiGift size={18} />
//                 Créer la carte cadeau
//               </>
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default GiftCardCreateForm;
